import { signOut } from 'next-auth/react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { GlobalRoles, LoginResponse, SignupResponse, MarketplaceAccount } from 'src/api/v1-api';
import { makeHeaders } from './fetchUtils';
import { getCookie, deleteCookie, setCookie, getExpires } from './cookiesUtils';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/nextjs';

const API_BASE = process.env.NEXT_PUBLIC_API_HOST + '/auth';

interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  date_joined: Date;
  image: string;
  last_login: Date;
  global_roles: Record<string, string[]>;
  roles: Record<string, string[]>;
  marketplace_accounts: MarketplaceAccount[];
}
interface TokenResponse {
  access: string;
  access_expires: number;
  refresh: string;
  refresh_expires: number;
}

const makeUrl = (endpoint: string): string => {
  return API_BASE + endpoint;
};

const fetchLogin = (
  username: string,
  password: string,
  marketplaceSlug: string,
): Promise<Response> => {
  const url = makeUrl('/token/');
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ username, password, marketplace_slug: marketplaceSlug }),
    headers: makeHeaders(),
    credentials: 'include',
  });
};

const bodyRefreshToken = async (): Promise<Response> => {
  const url = makeUrl('/token/body-refresh/');
  const refresh_token = getCookie('refresh_token');

  if (!refresh_token) {
    return new Response('No refresh token found', { status: 400 });
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ refresh_token }),
      headers: makeHeaders(),
      credentials: 'include',
      signal: AbortSignal.timeout(15000),
    });

    return response;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        action: 'bodyRefreshToken',
        tokenExists: 'true',
        errorType: error instanceof Error ? error.name : 'Unknown bodyRefreshToken error',
      },
      extra: {
        url,
        timestamp: new Date().toISOString(),
        errorMessage: error instanceof Error ? error.message : 'Unknown bodyRefreshToken error',
      },
    });

    return new Response(
      JSON.stringify({
        error: 'Failed to refresh token',
        details: error instanceof Error ? error.message : 'Unknown error',
      }),
      {
        status: 503,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }
};

/**
 * Get the current marketplace ID from localStorage if available
 * This is used when the marketplace context is not accessible (e.g., in AuthProvider)
 */
function getCurrentMarketplaceId(): number | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const hostname = window.location.hostname;
    const cached = localStorage.getItem(`marketplace-${hostname}`);

    if (cached) {
      const { data } = JSON.parse(cached);
      return data?.id;
    }
  } catch (error) {
    return undefined;
  }

  return undefined;
}

async function fetchUser(token: string, marketplaceId?: number): Promise<Response> {
  let url = makeUrl('/me/');

  const mpId = marketplaceId ?? getCurrentMarketplaceId();
  if (mpId) {
    url += `?marketplace_id=${mpId}`;
  }

  return fetch(url, {
    method: 'GET',
    headers: makeHeaders(token, true),
  });
}

async function fetchValidateTicket(ticket: string): Promise<Response> {
  const url = `${process.env.NEXT_PUBLIC_API_HOST}/sso/validate-ticket/`;
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ ticket }),
    headers: makeHeaders(),
    credentials: 'include',
  });
}

type AuthContextProps = {
  isAuthenticated: boolean;
  authLoading: boolean;
  socialAuthLoading: boolean;
  userLoaded: boolean;
  user: User | null;
  login: (username: string, password: string, marketplaceSlug: string) => Promise<LoginResponse>;
  logout: () => Promise<boolean>;
  getToken: () => Promise<string>;
  getOptionalToken: () => Promise<string>;
  signup: (
    email: string,
    password: string,
    firstName: string | undefined,
    lastName: string | undefined,
    marketplaceSlug: string,
    marketingSlug?: string,
    recaptchaToken?: string,
  ) => Promise<SignupResponse>;
  ticketLogin: (ticket: string) => Promise<LoginResponse>;
  hasRole: (marketplaceId: string | number | null | undefined, role: string) => boolean;
  hasRoles: (marketplaceId: string | number | null | undefined, roles: string[]) => boolean;
  hasAdmin: (marketplaceId: string | number | null | undefined) => boolean;
  hasStaff: () => boolean;
  refreshToken: () => Promise<string>;
  updateUser: () => Promise<User | undefined>;
  getLomaSessionId: () => string | undefined;
};

const initialAuthContextProps: AuthContextProps = {
  isAuthenticated: false,
  authLoading: false,
  socialAuthLoading: false,
  userLoaded: false,
  user: null,
  login: () =>
    Promise.resolve({ access: '', refresh: '', access_expires: 0, refresh_expires: 0, detail: '' }),
  signup: () =>
    Promise.resolve({ access: '', refresh: '', access_expires: 0, refresh_expires: 0, detail: '' }),
  ticketLogin: () =>
    Promise.resolve({ access: '', refresh: '', access_expires: 0, refresh_expires: 0, detail: '' }),
  logout: () => Promise.resolve(false),
  getToken: () => Promise.resolve(''),
  getOptionalToken: () => Promise.resolve(''),
  hasRole: () => false,
  hasRoles: () => false,
  hasAdmin: () => false,
  hasStaff: () => false,
  refreshToken: () => Promise.resolve(''),
  updateUser: () => Promise.resolve(undefined),
  getLomaSessionId: () => undefined,
};

const AuthContext = React.createContext<AuthContextProps>(initialAuthContextProps);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): React.ReactElement | null => {
  const router = useRouter();
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [socialAuthLoading, setSocialAuthLoading] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  const [accessTokenExpiry, setAccessTokenExpiry] = useState<number>(0);
  const { access_token: queryAccessToken } = router.query;
  const { isReady: isRouterReady } = router;
  const posthog = usePostHog();

  // Token refresh lock to prevent multiple simultaneous refresh attempts
  const refreshPromiseRef = useRef<Promise<string> | null>(null);

  const setNotAuthenticated = useCallback((): void => {
    deleteCookie('refresh_token');
    setAccessToken('');
    setAccessTokenExpiry(0);
    setIsAuthenticated(false);
    setAuthLoading(false);
    setUser(null);
    setUserLoaded(true);
  }, []);

  const loginWithAccessToken = async (): Promise<void> => {
    try {
      setAuthLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/auth/access-token/?token=${queryAccessToken}`,
      );
      if (response.status !== 200) {
        return;
      }
      const tokenData: TokenResponse = await response.json();
      handleNewToken(tokenData);
      setCookie('refresh_token', tokenData.refresh, {
        expires: getExpires(tokenData.refresh_expires),
      });
      await initUser(tokenData.access);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          authFlow: 'loginWithAccessToken',
          component: 'AuthProvider',
        },
        extra: {
          hasQueryAccessToken: !!queryAccessToken,
          timestamp: new Date().toISOString(),
        },
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const initUser = useCallback(async (token: string): Promise<void> => {
    const resp = await fetchUser(token);
    const user = await resp.json();
    setUser(user);
    setUserLoaded(true);
  }, []);

  useEffect(() => {
    if (user) {
      posthog?.identify(user.id, {
        email: user.email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const accessTokenIsValid = useCallback((): boolean => {
    if (accessToken === '') {
      return false;
    }
    // accessTokenExpiry is in seconds, convert to milliseconds for Date
    const expiry = new Date(accessTokenExpiry * 1000);
    // Add 3 second buffer to proactively refresh before expiry
    const bufferMs = 3000;
    return expiry.getTime() > Date.now() + bufferMs;
  }, [accessToken, accessTokenExpiry]);

  const handleNewToken = useCallback((data: TokenResponse): void => {
    setAccessToken(data.access);
    setAccessTokenExpiry(data.access_expires);
    setIsAuthenticated(true);
  }, []);

  const refreshToken = useCallback(async (): Promise<string> => {
    // If a refresh is already in progress, return that promise
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    // Create the promise first (don't invoke as IIFE)
    const executeRefresh = async (): Promise<string> => {
      try {
        const refresh_token = getCookie('refresh_token');

        if (!refresh_token) {
          if (queryAccessToken) {
            loginWithAccessToken();
            return '';
          }
          setNotAuthenticated();
          return '';
        }

        const resp = await bodyRefreshToken();

        if (!resp.ok) {
          Sentry.captureMessage('Token refresh failed - stale or invalid refresh token', {
            level: 'warning',
            tags: {
              authFlow: 'refreshToken',
              component: 'AuthProvider',
              httpStatus: resp.status.toString(),
            },
            extra: {
              statusText: resp.statusText,
              hasRefreshToken: true,
              timestamp: new Date().toISOString(),
            },
          });
          setNotAuthenticated();
          return '';
        }

        const tokenData = await resp.json();

        handleNewToken(tokenData);
        setCookie('refresh_token', tokenData.refresh, {
          expires: getExpires(tokenData.refresh_expires),
        });
        if (user === null) {
          await initUser(tokenData.access);
        }
        return tokenData.access;
      } finally {
        // Clear the refresh promise when done
        refreshPromiseRef.current = null;
      }
    };

    // Store the promise BEFORE it starts executing to prevent race conditions
    const refreshPromise = executeRefresh();
    refreshPromiseRef.current = refreshPromise;

    return refreshPromise;
  }, [setNotAuthenticated, handleNewToken, user, initUser, queryAccessToken]);

  const initAuth = useCallback(async (): Promise<void> => {
    if (!accessTokenIsValid()) {
      await refreshToken();
    } else {
      setIsAuthenticated(true);
    }
    setAuthLoading(false);
  }, [accessTokenIsValid, refreshToken, setAuthLoading]);

  useEffect(() => {
    if (!isRouterReady) return;
    initAuth().catch((error) => console.error(error));
  }, [initAuth, isRouterReady]);

  const ticketLogin = async (ticket: string): Promise<any> => {
    setSocialAuthLoading(true);
    const resp = await fetchValidateTicket(ticket);
    const data = await resp.json();

    if (resp.ok) {
      if (!data.error) {
        handleNewToken(data);
        setCookie('refresh_token', data.refresh, {
          expires: getExpires(data.refresh_expires),
        });
        await initUser(data.access);
        setAuthLoading(false);
        return data;
      }
    } else {
      Sentry.captureException(new Error(data.error), {
        tags: {
          authFlow: 'ticketLogin',
          component: 'AuthProvider',
          errorType: 'ticket_login_failed',
        },
        extra: {
          ticket,
        },
      });
    }
    setNotAuthenticated();
    return data;
  };

  const login = async (
    username: string,
    password: string,
    marketplaceSlug: string,
  ): Promise<LoginResponse> => {
    setAuthLoading(true);
    username = username.toLowerCase();
    const resp = await fetchLogin(username, password, marketplaceSlug);
    if (resp.ok) {
      const tokenData = await resp.json();
      handleNewToken(tokenData);
      setCookie('refresh_token', tokenData.refresh, {
        expires: getExpires(tokenData.refresh_expires),
      });
      await initUser(tokenData.access);
      setAuthLoading(false);
      return tokenData;
    } else {
      setNotAuthenticated();
    }
    return resp.json();
  };

  const getOptionalToken = async (): Promise<string> => {
    if (accessTokenIsValid()) {
      return accessToken;
    } else {
      const token = await refreshToken();
      if (!token) {
        setNotAuthenticated();
      }
      return token;
    }
  };

  const getToken = async (): Promise<string> => {
    // Returns an access token if there's one or refetches a new one
    if (accessTokenIsValid()) {
      return accessToken;
    } else {
      const token = await refreshToken();

      if (!token) {
        const error = new Error('Failed to refresh authentication token. Please log in again.');
        const refreshTokenCookie = getCookie('refresh_token');

        // Mark this as an auth error so callers can distinguish it from other errors
        (error as any).isAuthError = true;
        (error as any).status = 401;

        Sentry.captureException(error, {
          // If user exists, this is a real error; otherwise it's just a warning (user not logged in)
          // TODO: Split this logic out into separate functions for getting token for split routes (allow unauth and auth) vs just auth routes
          level: user ? 'error' : 'warning',
          tags: {
            component: 'AuthProvider',
            authFlow: 'getToken',
            errorType: 'token_refresh_failed',
            hasUser: user ? 'true' : 'false',
            isAuthenticated: isAuthenticated.toString(),
            hasRefreshTokenCookie: refreshTokenCookie ? 'true' : 'false',
            hasAccessToken: accessToken ? 'true' : 'false',
            userLoaded: userLoaded.toString(),
            routePath: router.pathname || 'unknown',
          },
          extra: {
            user: user
              ? {
                  id: user.id,
                  email: user.email,
                  username: user.username,
                  isActive: user.is_active,
                  isStaff: user.is_staff,
                  marketplaceAccountsCount: user.marketplace_accounts?.length || 0,
                  rolesCount: Object.keys(user.roles || {}).length,
                  globalRolesCount: Object.keys(user.global_roles || {}).length,
                }
              : null,
            authState: {
              isAuthenticated,
              authLoading,
              socialAuthLoading,
              userLoaded,
              accessTokenExpiry: accessTokenExpiry ? getExpires(accessTokenExpiry) : null,
              hasAccessToken: !!accessToken,
              hasRefreshTokenCookie: !!refreshTokenCookie,
              accessTokenIsValid: accessTokenIsValid(),
            },
            routerInfo: {
              pathname: router.pathname,
              query: router.query,
              asPath: router.asPath,
              isReady: router.isReady,
            },
            timestamp: new Date().toISOString(),
          },
        });
        return '';
        // TODO: Enable once we are sure these are all valid cases
        // throw error;
      }

      return token;
    }
  };

  const logout = async (): Promise<boolean> => {
    setAuthLoading(true);

    let token = '';
    try {
      token = await getToken();
    } catch (error) {
      // Unable to get token, proceed with local logout only
      Sentry.captureException(error, {
        tags: {
          component: 'AuthProvider',
          errorType: 'logout_token_unavailable',
        },
        extra: {
          message: 'Unable to get token for logout, proceeding with local logout only',
        },
      });
      await signOut();
      setNotAuthenticated();
      return true;
    }

    const url = makeUrl('/token/logout/');
    const response = await fetch(url, {
      method: 'POST',
      headers: makeHeaders(token, true),
      credentials: 'include',
    });

    if (!response.ok) {
      Sentry.captureMessage('Logout request failed', {
        level: 'error',
        tags: {
          authFlow: 'logout',
          component: 'AuthProvider',
          httpStatus: response.status.toString(),
        },
        extra: {
          statusText: response.statusText,
          hasToken: !!token,
          timestamp: new Date().toISOString(),
        },
      });
      setAuthLoading(false);
      return false;
    }
    await signOut();
    setNotAuthenticated();
    return true;
  };

  const signup = async (
    email: string,
    password: string,
    firstName: string | undefined,
    lastName: string | undefined,
    marketplaceSlug: string,
    marketingSlug?: string,
    recaptchaToken?: string,
  ): Promise<SignupResponse> => {
    email = email.toLowerCase();
    let url = makeUrl('/register/');

    if (marketplaceSlug === 'admin') {
      url = makeUrl('/onboarding-register/');
    }

    const requestBody: any = {
      email: email,
      username: email,
      password: password,
      first_name: firstName,
      last_name: lastName,
      marketplace_slug: marketplaceSlug,
    };

    if (recaptchaToken) {
      requestBody.recaptcha_token = recaptchaToken;
    }

    if (marketingSlug) {
      requestBody.marketing_slug = marketingSlug;
    }

    const resp = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: makeHeaders(),
      credentials: 'include',
    });

    const data = await resp.json();

    if (resp.ok) {
      const response = await login(email, password, marketplaceSlug);
      response.has_user_roles = data['has_user_roles'];
      return response;
    } else {
      return data;
    }
  };

  const hasRole = (marketplaceId: string | number | undefined | null, role: string): boolean => {
    if (!user || !marketplaceId || !isAuthenticated) return false;

    if (!user.roles[marketplaceId]) return false;

    if (user.roles[marketplaceId].includes(role)) {
      return true;
    }

    const user_global_roles = user.global_roles?.[marketplaceId] ?? [];
    if (user.global_roles[GlobalRoles.Admin]) {
      user_global_roles.push(GlobalRoles.Admin);
    }

    if (user.global_roles[GlobalRoles.Configuration]) {
      user_global_roles.push(GlobalRoles.Configuration);
    }

    if (!user_global_roles) return false;
    return user_global_roles.includes(role);
  };

  const hasRoles = (
    marketplaceId: string | number | undefined | null,
    roles: string[],
  ): boolean => {
    if (!user || !marketplaceId) return false;
    if (roles === undefined) return false;
    if (roles.length === 0) return true;
    if (!user.roles || typeof user.roles !== 'object') return false;
    const userRolesForMarketplace = user.roles[marketplaceId];
    if (userRolesForMarketplace && roles.some((role) => userRolesForMarketplace.includes(role))) {
      return true;
    } else {
      return false;
    }
  };

  const hasStaff = (): boolean => {
    if (!user) return false;
    return user.is_staff;
  };

  const hasAdmin = (marketplaceId: string | number | undefined | null): boolean => {
    return hasRole(marketplaceId?.toString() ?? '', 'Admin');
  };

  const updateUser = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) {
        console.error('No token available to fetch user data');
        return;
      }

      const response = await fetchUser(token);
      if (!response.ok) {
        Sentry.captureMessage('Failed to fetch updated user data', {
          level: 'error',
          tags: {
            authFlow: 'updateUser',
            component: 'AuthProvider',
            httpStatus: response.status.toString(),
          },
          extra: {
            statusText: response.statusText,
            timestamp: new Date().toISOString(),
          },
        });
        return;
      }

      const userData = await response.json();
      setUser(userData);

      posthog?.identify(userData.id, {
        email: userData.email,
      });

      return userData;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          authFlow: 'updateUser',
          component: 'AuthProvider',
        },
        extra: {
          timestamp: new Date().toISOString(),
        },
      });
      throw error;
    }
  }, [getToken]);

  const getLomaSessionId = (): string | undefined => {
    // Only used for cart management of non-logged in users
    if (user) return undefined;

    // Only proceed with localStorage if in browser environment
    if (typeof window === 'undefined') return undefined;

    try {
      // Try to get existing session id
      const sessionId = localStorage.getItem('lomasessionid');
      if (sessionId) return sessionId;

      // If no session id, create a new one
      const newSessionId = uuidv4();
      try {
        localStorage.setItem('lomasessionid', newSessionId);
      } catch (error) {
        console.warn('Unable to save session ID to localStorage:', error);
      }
      return newSessionId;
    } catch (error) {
      console.warn('Unable to access localStorage for session ID:', error);
      // Return a new session ID even if we can't store it
      return uuidv4();
    }
  };

  const value = {
    isAuthenticated,
    user,
    authLoading,
    userLoaded,
    login,
    logout,
    getToken,
    getOptionalToken,
    signup,
    hasRole,
    hasRoles,
    hasAdmin,
    hasStaff,
    ticketLogin,
    socialAuthLoading,
    refreshToken,
    updateUser,
    getLomaSessionId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
